import i18n from 'i18n-js';

import { AppThunkAction, AppThunkActionAsync } from 'store';

import { TableMaps } from 'api/controllers/tableMaps';
import { EditorTable } from 'components/tableMaps/CreateTableMapWizard/context';
import { MapFeature } from 'components/tableMaps/CreateTableMapWizard/features';
import { EditorArea, SizeMeters } from 'components/tableMaps/CreateTableMapWizard/mapGeometry';
import { MapOpening } from 'components/tableMaps/CreateTableMapWizard/openings';
import {
    tableChanged,
    tablesThatLostQrCode,
} from 'components/tableMaps/CreateTableMapWizard/tableEdit';
import {
    editorAreaToAreaModel,
    editorTableToMapTableModel,
    tablesMapDtoToDetails,
    tablesMapDtoToListItem,
    MapTableDTO,
    TablesMapDTO,
} from './mappers';
import { TableMapsAction } from './actions';
import globalStore from '../globalStore';

export class QrConflictError extends Error {
    readonly qrConflictLabels: string[];
    constructor(labels: string[]) {
        super(`QR_CONFLICT:${labels.join(',')}`);
        this.name = 'QrConflictError';
        this.qrConflictLabels = labels;
    }
}

/** The axios interceptor rejects with the server message itself, so errors arrive as strings. */
export function errorMessage(err: unknown): string | null {
    if (typeof err === 'string') return err;
    const data = (err as { response?: { data?: { Message?: unknown; message?: unknown } } })
        ?.response?.data;
    const msg = data?.Message ?? data?.message ?? (err as { message?: unknown })?.message;
    return typeof msg === 'string' ? msg : null;
}

/** Resource `QrCodeAlreadyAssigned`, in English and German. */
function isQrConflictError(err: unknown): boolean {
    const msg = errorMessage(err)?.toLowerCase();
    return (
        !!msg &&
        (msg.includes('qr code already assigned') || msg.includes('qr-code ist bereits zugewiesen'))
    );
}

/** Every TablesMapFeature* rejection names a "room feature" (en) / "Raumelement" (de). */
export function isFeatureSaveError(err: unknown): boolean {
    const msg = errorMessage(err)?.toLowerCase();
    return !!msg && (msg.includes('room feature') || msg.includes('raumelement'));
}

interface CreateTableMapParams {
    name: string;
    isActive: boolean;
    isDraft: boolean;
    draftStep: number | null;
    entrances: MapOpening[];
    features: MapFeature[];
    size: SizeMeters;
    area: EditorArea;
    tables: EditorTable[];
    gridEnabled: boolean;
}

interface UpdateTableMapParams {
    id: string;
    name: string;
    isActive: boolean;
    isDraft: boolean;
    draftStep: number | null;
    entrances: MapOpening[];
    features: MapFeature[];
    size: SizeMeters;
    area: EditorArea;
    tables: EditorTable[];
    gridEnabled: boolean;
    originalTables: EditorTable[];
    /** The save turns a draft into a published map, so its tables claim their QR codes only now. */
    publishesDraft?: boolean;
}

export class TableSaveError extends Error {
    readonly serverMessage: string | null;
    constructor(reason: unknown) {
        const serverMessage = errorMessage(reason);
        super(serverMessage ?? 'TABLE_SAVE');
        this.name = 'TableSaveError';
        this.serverMessage = serverMessage;
    }
}

function buildTablesMapDto(mapDto: TablesMapDTO, tableDtos: MapTableDTO[]): TablesMapDTO {
    return { ...mapDto, tables: tableDtos };
}

export const actionCreators = {
    getTableMaps: (): AppThunkActionAsync<TableMapsAction> => (dispatch) => {
        dispatch({ type: 'TABLE_MAPS_GET_START' });
        return TableMaps.getTablesMaps()
            .then((dtos) => {
                const list = dtos.map(tablesMapDtoToListItem);
                dispatch({ type: 'TABLE_MAPS_GET_SUCCESS', list });
            })
            .catch(() => dispatch({ type: 'TABLE_MAPS_GET_ERROR' }));
    },

    deleteTableMap:
        (id: string): AppThunkAction<TableMapsAction> =>
        (dispatch) => {
            TableMaps.deleteTablesMap(id)
                .then(() => dispatch({ type: 'TABLE_MAP_DELETE', id }))
                .catch((e) =>
                    globalStore.actionCreators.showToaster(
                        'error',
                        errorMessage(e) ?? i18n.t('common.somethingWrong')
                    )(dispatch)
                );
        },

    setTableMapActive:
        (id: string, isActive: boolean): AppThunkActionAsync<TableMapsAction> =>
        async (dispatch) => {
            try {
                const current = await TableMaps.getTablesMap(id);
                await TableMaps.updateTablesMap(id, {
                    name: current.name,
                    isActive,
                    isDraft: current.isDraft,
                    draftStep: current.draftStep,
                    entrances: current.entrances,
                    features: current.features,
                    size: current.size,
                    area: current.area,
                });

                const updatedDto = await TableMaps.getTablesMap(id);
                dispatch({
                    type: 'TABLE_MAP_UPDATE',
                    item: tablesMapDtoToListItem(updatedDto),
                    details: tablesMapDtoToDetails(updatedDto),
                });
            } catch (e) {
                globalStore.actionCreators.showToaster(
                    'error',
                    errorMessage(e) ?? i18n.t('common.somethingWrong')
                )(dispatch);
            }
        },

    duplicateTableMap:
        (id: string): AppThunkActionAsync<TableMapsAction> =>
        async (dispatch) => {
            try {
                const copyDto = await TableMaps.copyTablesMap(id);
                dispatch({ type: 'TABLE_MAP_CREATE', item: tablesMapDtoToListItem(copyDto) });
                globalStore.actionCreators.showToaster(
                    'success',
                    i18n.t('tableMaps.duplicateSuccess', { name: copyDto.name })
                )(dispatch);
            } catch (e) {
                globalStore.actionCreators.showToaster(
                    'error',
                    errorMessage(e) ?? i18n.t('common.somethingWrong')
                )(dispatch);
                actionCreators.getTableMaps()(dispatch);
            }
        },

    createTableMap:
        (params: CreateTableMapParams): AppThunkActionAsync<TableMapsAction, string> =>
        async (dispatch) => {
            const mapDto = await TableMaps.createTablesMap({
                name: params.name,
                isActive: params.isActive,
                isDraft: params.isDraft,
                draftStep: params.draftStep,
                entrances: params.entrances,
                features: params.features,
                size: params.size,
                area: editorAreaToAreaModel(params.area),
            });

            const tableResults = await Promise.allSettled(
                params.tables.map((t) =>
                    TableMaps.createMapTable(mapDto.id, editorTableToMapTableModel(t))
                )
            );

            const qrConflictLabels: string[] = [];
            const otherErrors: unknown[] = [];
            params.tables.forEach((t, i) => {
                const result = tableResults[i];
                if (result.status === 'rejected') {
                    if (isQrConflictError(result.reason)) {
                        qrConflictLabels.push(t.label);
                    } else {
                        otherErrors.push(result.reason);
                    }
                }
            });
            if (qrConflictLabels.length > 0 || otherErrors.length > 0) {
                // A half-created map would keep holding the QR codes its tables did get, so a retry could never succeed.
                await TableMaps.deleteTablesMap(mapDto.id).catch(() => undefined);
            }
            if (qrConflictLabels.length > 0) {
                throw new QrConflictError(qrConflictLabels);
            }
            if (otherErrors.length > 0) {
                throw new TableSaveError(otherErrors[0]);
            }

            const tableDtos = tableResults.map(
                (r) => (r as PromiseFulfilledResult<MapTableDTO>).value
            );

            const fullDto = buildTablesMapDto(mapDto, tableDtos);
            const item = tablesMapDtoToListItem(fullDto);
            const details = tablesMapDtoToDetails(fullDto);
            details.gridEnabled = params.gridEnabled;

            dispatch({ type: 'TABLE_MAP_CREATE', item });
            dispatch({ type: 'TABLE_MAP_DETAILS_SET', details });

            return mapDto.id;
        },

    updateTableMap:
        (params: UpdateTableMapParams): AppThunkActionAsync<TableMapsAction, string[]> =>
        async (dispatch) => {
            await TableMaps.updateTablesMap(params.id, {
                name: params.name,
                isActive: params.isActive,
                isDraft: params.isDraft,
                draftStep: params.draftStep,
                entrances: params.entrances,
                features: params.features,
                size: params.size,
                area: editorAreaToAreaModel(params.area),
            });

            const originalById = new Map(params.originalTables.map((t) => [t.id, t]));
            const currentIds = new Set(params.tables.map((t) => t.id));

            const deletedTables = params.originalTables.filter((t) => !currentIds.has(t.id));
            const deletePromises = deletedTables.map((t) =>
                TableMaps.deleteMapTable(params.id, t.id)
            );

            const newTables = params.tables.filter((t) => !originalById.has(t.id));

            const changedTables = params.tables.filter((t) => {
                const orig = originalById.get(t.id);
                return orig !== undefined && tableChanged(t, orig);
            });

            type TableOp = { table: EditorTable; send: (t: EditorTable) => Promise<MapTableDTO> };
            const tableOps: TableOp[] = [
                ...newTables.map((table) => ({
                    table,
                    send: (t: EditorTable) =>
                        TableMaps.createMapTable(params.id, editorTableToMapTableModel(t)),
                })),
                ...changedTables.map((table) => ({
                    table,
                    send: (t: EditorTable) =>
                        TableMaps.updateMapTable(params.id, t.id, editorTableToMapTableModel(t)),
                })),
            ];
            // A draft reserves nothing, so a published map may have taken a code meanwhile. The
            // published map wins: the table is saved without it rather than failing a map that is
            // already published by the PUT above.
            const sendTable = ({ table, send }: TableOp) =>
                params.publishesDraft
                    ? send(table).catch((e) =>
                          isQrConflictError(e)
                              ? send({ ...table, qrCode: null, isActive: false })
                              : Promise.reject(e)
                      )
                    : send(table);

            const deleteResults = await Promise.allSettled(deletePromises);
            const tableResults = await Promise.allSettled(tableOps.map(sendTable));

            const qrConflictLabels: string[] = [];
            const otherErrors: unknown[] = [];
            tableResults.forEach((result, i) => {
                if (result.status === 'rejected') {
                    if (isQrConflictError(result.reason)) {
                        qrConflictLabels.push(tableOps[i].table.label);
                    } else {
                        otherErrors.push(result.reason);
                    }
                }
            });
            deleteResults.forEach((result) => {
                if (result.status === 'rejected') {
                    otherErrors.push(result.reason);
                }
            });
            if (qrConflictLabels.length > 0) {
                throw new QrConflictError(qrConflictLabels);
            }
            if (otherErrors.length > 0) {
                throw new TableSaveError(otherErrors[0]);
            }

            const updatedDto = await TableMaps.getTablesMap(params.id);
            const item = tablesMapDtoToListItem(updatedDto);
            const details = tablesMapDtoToDetails(updatedDto);
            details.gridEnabled = params.gridEnabled;

            dispatch({ type: 'TABLE_MAP_UPDATE', item, details });

            return tablesThatLostQrCode(params.tables, details.tables);
        },

    getTableMapDetails:
        (id: string): AppThunkActionAsync<TableMapsAction> =>
        async (dispatch) => {
            try {
                const dto = await TableMaps.getTablesMap(id);
                const details = tablesMapDtoToDetails(dto);
                dispatch({ type: 'TABLE_MAP_DETAILS_SET', details });
            } catch {
                // Callers read details[id] and show their own not-found state when it never arrives.
            }
        },
};
